import React, { useState, useEffect, useRef } from 'react';
import { ClipboardCheck } from 'lucide-react';
import BuiltInFormRenderer from '../DynamicForm/BuiltInFormRenderer';
import { useRetrofitPhotoUpload } from '../../hooks/useRetrofitPhotoUpload';
import { getRetrofitSurveyAddress } from '../../utils/retrofitSurveyStorage';
import PhotoGalleryModal from '../PhotoGalleryModal';
import { siteAssessmentSchema } from '../../schemas/siteAssessmentSchema';
import type { FieldSchema } from '../../types/formSchema';
import { deletePhotoFromSupabase } from '../../utils/supabaseStorage';

interface SiteAssessmentFormSchemaVersionProps {
  surveyData: any;
  onDataUpdate: (updater: (prev: any) => any, section: string) => void;
  customFields: FieldSchema[];
  hiddenFields: string[];
  hiddenSections: string[];
  sectionOrder: string[];
  builtInFieldOrder: string[];
  onNavigateToPrevious?: () => void;
  onNavigateToNext?: () => void;
}

export const SiteAssessmentFormSchemaVersion: React.FC<SiteAssessmentFormSchemaVersionProps> = ({
  surveyData,
  onDataUpdate,
  customFields,
  hiddenFields,
  hiddenSections,
  sectionOrder,
  builtInFieldOrder,
  onNavigateToPrevious,
  onNavigateToNext,
}) => {
  // Get the survey ID for photo uploads
  const surveyId = surveyData?.id;
  
  // State for built-in form fields
  const [formData, setFormData] = useState<Record<string, any>>({});
  
  // State for built-in photo fields
  const [photos, setPhotos] = useState<Record<string, any[]>>({});
  
  // State for custom fields (includes both values and photos with _photos suffix)
  const [customFieldData, setCustomFieldData] = useState<Record<string, any>>({});
  
  // Photo gallery state
  const [selectedGallery, setSelectedGallery] = useState<string | null>(null);
  
  // Track internal updates to prevent reload loops
  const isInternalUpdate = useRef(false);
  
  // Initialize photo upload queue with explicit surveyId and property name for better file organization
  const propertyName = surveyData ? getRetrofitSurveyAddress(surveyData) : undefined;
  const { queuePhotoUpload } = useRetrofitPhotoUpload(surveyId, propertyName);

  // ========== LOAD BUILT-IN FORM DATA ==========
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[SiteAssessmentForm] Skipping main data reload - internal update');
      return;
    }
    
    if (surveyData?.site_assessment) {
      console.log('[SiteAssessmentForm] Loading built-in data from surveyData');
      const sectionData = surveyData.site_assessment;
      
      // Extract non-photo fields
      const newFormData: Record<string, any> = {};
      const newPhotos: Record<string, any[]> = {};
      
      Object.entries(sectionData).forEach(([key, value]) => {
        // Check if it's a photo field (array type from schema)
        const schemaField = siteAssessmentSchema.sections
          .flatMap(s => s.fields)
          .find(f => f.id === key);
        
        if (schemaField?.type === 'photo' && Array.isArray(value)) {
          newPhotos[key] = value;
        } else if (value !== undefined && value !== null) {
          newFormData[key] = value;
        }
      });
      
      setFormData(newFormData);
      setPhotos(newPhotos);
    }
  }, [surveyData]);
  
  // ========== LOAD CUSTOM FIELD DATA (WITH PHOTO FIX) ==========
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[SiteAssessmentForm] Skipping custom field reload - internal update');
      return;
    }
    
    if (surveyData?.site_assessment && customFields && customFields.length > 0) {
      console.log('[SiteAssessmentForm] Loading custom field data');
      const existingData: Record<string, any> = {};
      
      customFields.forEach(field => {
        // Load the field value
        const value = (surveyData.site_assessment as any)?.[field.id];
        if (value !== undefined) {
          existingData[field.id] = value;
        }
        
        // ⭐ FIX 3: Also load photos if they exist (stored with _photos suffix)
        const photosFieldId = `${field.id}_photos`;
        const photoValue = (surveyData.site_assessment as any)?.[photosFieldId];
        if (photoValue !== undefined) {
          console.log('[SiteAssessmentForm] Loading photos for custom field:', field.id, '→', photosFieldId);
          existingData[photosFieldId] = photoValue;
        }
      });
      
      if (Object.keys(existingData).length > 0) {
        console.log('[SiteAssessmentForm] Loaded custom field data:', Object.keys(existingData));
        setCustomFieldData(existingData);
      }
    }
  }, [surveyData?.site_assessment, customFields]);
  
  // Reset internal update flag after all data loaders have run
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[SiteAssessmentForm] Resetting internal update flag');
      isInternalUpdate.current = false;
    }
  }, [surveyData]);
  
  // ========== HANDLE BUILT-IN FORM DATA CHANGES ==========
  const handleFormDataChange = (fieldId: string, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [fieldId]: value };
      
      // Trigger autosave
      if (onDataUpdate) {
        setTimeout(() => {
          isInternalUpdate.current = true;
          onDataUpdate((prevData) => ({
            ...prevData,
            site_assessment: {
              ...prevData.site_assessment,
              ...updated,
              ...photos,
              ...customFieldData
            }
          }), 'site_assessment');
        }, 50);
      }
      
      return updated;
    });
  };
  
  // ========== HANDLE CUSTOM FIELD CHANGES ==========
  const handleCustomFieldChange = (fieldId: string, value: any) => {
    setCustomFieldData(prev => {
      const updated = { ...prev, [fieldId]: value };
      
      // Trigger autosave
      if (onDataUpdate) {
        setTimeout(() => {
          isInternalUpdate.current = true;
          onDataUpdate((prevData) => ({
            ...prevData,
            site_assessment: {
              ...prevData.site_assessment,
              ...formData,
              ...photos,
              ...updated
            }
          }), 'site_assessment');
        }, 50);
      }
      
      return updated;
    });
  };
  
  // ========== HANDLE PHOTO CHANGES ==========
  const handlePhotosChange = (fieldId: string, newPhotos: any[]) => {
    console.log(`[SiteAssessmentForm] Handling photos change for ${fieldId}:`, newPhotos);
    
    // Transform photos to proper format
    const newPhotoData = newPhotos.map(photo => {
      let dataUrl = photo.dataUrl || photo.base64String;
      
      // Ensure proper data URL format
      if (dataUrl && !dataUrl.startsWith('data:')) {
        const format = photo.format || 'jpeg';
        dataUrl = `data:image/${format};base64,${dataUrl}`;
      }
      
      return {
        id: photo.id || `photo_${Date.now()}_${Math.random()}`,
        displayId: photo.displayId || photo.id,
        dataUrl,
        url: photo.url || '',
        uploading: false,
        format: photo.format || 'jpeg',
      };
    });
    
    // Update photos state
    setPhotos(prev => {
      const updated = { ...prev, [fieldId]: newPhotoData };
      
      // Immediately save with dataUrl
      if (onDataUpdate) {
        isInternalUpdate.current = true;
        onDataUpdate((prevData) => ({
          ...prevData,
          site_assessment: {
            ...prevData.site_assessment,
            ...formData,
            ...updated,
            ...customFieldData
          }
        }), 'site_assessment');
      }
      
      return updated;
    });
    
    // Queue for upload to Supabase (background process)
    newPhotoData.forEach((photo) => {
      if (photo.dataUrl && !photo.url) {
        queuePhotoUpload(
          photo.displayId,
          photo.dataUrl,
          'site_assessment',
          fieldId,
          (url: string) => {
            const uploadedPhoto = { ...photo, url };
            // Update with Supabase URL after upload
            setPhotos(prev => ({
              ...prev,
              [fieldId]: (prev[fieldId] || []).map(p =>
                p.id === uploadedPhoto.id
                  ? { ...p, url: uploadedPhoto.url, uploading: false }
                  : p
              ),
            }));
          },
          (error: string) => console.error('Photo upload failed:', error)
        );
      }
    });
  };
  
  // ========== HANDLE GALLERY OPEN/CLOSE ==========
  const handleGalleryOpen = (fieldId: string) => {
    setSelectedGallery(fieldId);
  };
  
  const handleGalleryClose = () => {
    setSelectedGallery(null);
  };

  const removePhoto = async (photoType: string, displayIdToRemove: string) => {
    const currentPhotos = photos[photoType] || [];
    const photoToDelete = currentPhotos.find((p: any) => (p.displayId || p.id) === displayIdToRemove);
    if (photoToDelete?.fileName || photoToDelete?.url) {
      let filePath = photoToDelete.fileName || photoToDelete.url.split('/storage/v1/object/public/')[1]?.split('/').slice(1).join('/');
      if (filePath) try { await deletePhotoFromSupabase(filePath, 'retrofit-survey-images'); } catch (e) {}
    }
    const filteredPhotos = currentPhotos.filter((p: any) => (p.displayId || p.id) !== displayIdToRemove);
    setPhotos(prev => ({ ...prev, [photoType]: filteredPhotos }));
    if (onDataUpdate) {
      onDataUpdate((prevData: any) => ({
        ...prevData,
        site_assessment: {
          ...prevData.site_assessment,
          ...photos,
          [photoType]: filteredPhotos
        }
      }), 'site_assessment');
    }
  };

  // ========== HANDLE FORM SUBMIT ==========
  const handleSubmit = (data: Record<string, any>) => {
    console.log('[SiteAssessmentForm] Form submitted:', data);
    // Navigation handled by navigation buttons
  };
  
  // Combine all data for form value handling
  const allFormData = { ...formData, ...customFieldData };
  
  return (
    <>
      <BuiltInFormRenderer
        schema={siteAssessmentSchema}
        formData={allFormData}
        photos={photos}
        onFormDataChange={(fieldId, value) => {
          // ⭐ FIX 1: Check if it's a custom field (strip _photos suffix for comparison)
          const baseFieldId = fieldId.replace(/_photos$/, '');
          const isCustomField = customFields.some(f => f.id === baseFieldId);
          
          if (isCustomField) {
            handleCustomFieldChange(fieldId, value);
          } else {
            handleFormDataChange(fieldId, value);
          }
        }}
        onPhotosChange={handlePhotosChange}
        onSubmit={handleSubmit}
        customFields={customFields}
        hiddenFields={hiddenFields}
        hiddenSections={hiddenSections}
        sectionOrder={sectionOrder}
        builtInFieldOrder={builtInFieldOrder}
        navigationButtons={{
          previous: onNavigateToPrevious ? {
            label: '← Previous',
            onClick: onNavigateToPrevious
          } : undefined,
          next: onNavigateToNext ? {
            label: 'Next →',
            onClick: onNavigateToNext
          } : undefined
        }}
        onGalleryOpen={handleGalleryOpen}
        selectedGallery={selectedGallery}
        formIcon={<ClipboardCheck className="w-8 h-8" />}
      />
      
      {selectedGallery && photos[selectedGallery] && (
        <PhotoGalleryModal
          isOpen={true}
          onClose={handleGalleryClose}
          photos={photos[selectedGallery].map((photo: any) => ({
            id: photo.displayId || photo.id,
            url: photo.url || photo.dataUrl,
            uploading: photo.uploading,
            error: photo.error
          }))}
          sectionTitle={`${selectedGallery} Photos`}
          onDeletePhoto={(photoId) => removePhoto(selectedGallery, photoId)}
          onUpdateNote={(photoId, note) => {
            setPhotos(current => ({
              ...current,
              [selectedGallery]: current[selectedGallery].map(p =>
                (p.displayId || p.id) === photoId ? { ...p, note } : p
              )
            }));
            
            // Update surveyData
            if (onDataUpdate) {
              onDataUpdate((prevData) => ({
                ...prevData,
                site_assessment: {
                  ...prevData.site_assessment,
                  [selectedGallery]: (photos[selectedGallery] || []).map(p =>
                    (p.displayId || p.id) === photoId ? { ...p, note } : p
                  )
                }
              }), 'site_assessment');
            }
          }}
        />
      )}
    </>
  );
};
